import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { FinancialMetricsProps, ReportData } from '../data/types';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors } from '../data/designTokens';
import { FINANCIAL_METRIC_DISPLAY_LABELS } from '../../../utils/constants/commonConstants';

const fmtCell = (v: number | string | null, unit?: string): string => {
    if (v == null || v === '') return '—';
    if (typeof v === 'number') {
        const formatted = v.toLocaleString('en-IN', { maximumFractionDigits: unit === '%' ? 1 : 2 });
        return unit ? `${formatted}${unit}` : formatted;
    }
    return String(v);
};

const dataColumns = (fm: FinancialMetricsProps) => fm.headers.filter((h) => h.key !== 'metricName').slice(-4);

// ──────────────────────────── Screen ────────────────────────────

const ScreenFinancialMetrics: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const fm = data.financialMetrics;
    if (!fm) return null;
    const cols = dataColumns(fm);
    const gridCols = `2fr repeat(${cols.length}, 1fr)`;
    return (
        <ScreenSection kicker="Financials" title="Key financial metrics" index={index}>
            <div style={{ marginTop: 8, borderTop: `1px solid ${colors.ink00}`, overflowX: 'auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: gridCols,
                        padding: '6px 8px',
                        borderBottom: `1px solid ${colors.ink00}`,
                        background: colors.surfaceAlt,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: colors.ink40,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        Metric
                    </div>
                    {cols.map((c) => (
                        <div
                            key={c.key}
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: colors.ink40,
                                textAlign: 'right',
                            }}
                        >
                            {c.name}
                        </div>
                    ))}
                </div>
                {fm.rows.map((row, ri) => (
                    <div
                        key={ri}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: gridCols,
                            // Bumped 5 → 9px vertical for the same row-breathing reason
                            // the PDF side was bumped to 7pt.
                            padding: '9px 8px',
                            borderBottom: `1px solid ${colors.rule}`,
                            background: ri % 2 === 0 ? colors.paper : colors.surfaceAlt,
                        }}
                    >
                        <div style={{ fontSize: 12, fontWeight: 500, color: colors.ink00 }}>
                            {FINANCIAL_METRIC_DISPLAY_LABELS[row.metricName] ?? row.metricName}
                        </div>
                        {cols.map((c) => (
                            <div
                                key={c.key}
                                style={{
                                    fontSize: 12,
                                    color: colors.ink10,
                                    textAlign: 'right',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {fmtCell(row.values[c.key], row.unit)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </ScreenSection>
    );
};

export const financialMetricsModule: ModuleDefinition = {
    meta: {
        id: 'financialMetrics',
        title: 'Key financial metrics',
        description: 'Revenue, margins, working capital, debt and tangible net worth overview.',
        dataSource: 'Audited Financials (financial metrics)',
    },
    // Presence of rows is not enough — Polus often returns the row scaffolding
    // (Revenue / EBITDA / etc.) with every cell null AND no data-year columns.
    // Render only when (a) there's at least one non-metricName column to fill
    // and (b) at least one cell has an actual value.
    hasData: (d) => {
        const fm = d.financialMetrics;
        if (!fm || fm.rows.length === 0) return false;
        const dataCols = fm.headers.filter((h) => h.key !== 'metricName');
        if (dataCols.length === 0) return false;
        return fm.rows.some((row) => Object.values(row.values ?? {}).some((v) => v != null && v !== ''));
    },
    Screen: ScreenFinancialMetrics,
};
